import React, { useEffect, useState } from "react";
import LegalbirdLoader from "@/src/components/ContentLoader/LegalbirdLoader";
import LayoutProtectedRoutes from "@/src/containers/Layouts/LayoutProtectedRoutes";
import { Link, useNavigate } from "react-router";
import { getCaseLink, getProductData, getProductNameBy } from "@/src/services/productService";
import { Button, Paper, Stack, TextField, Typography } from "@mui/material";
import HintBox from "@/src/components/Box/HintBox/HintBox";
import ButtonLoading from "@/src/components/Button/ButtonLoading";
import TextContainer from "@/src/components/Container/TextContainer";
import { STATUS_COMPLETED, STATUS_EXTRAJUDICIAL_COMMUNICATION_SENT } from "@/src/services/litigationCase/leadStatus";
import { STATUS_ADMINISTRATIVE_PROCEEDINGS } from "@/src/services/administrativeLawLitigationCase/leadStatus";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { fetchResource, queryKeys, updateResource } from "@/src/services/reactQuery/reactQueryService";
import useProductParams from "@/src/hooks/useProductParams";
import ScrollToTop from "@/src/services/ScrollToTop";

export default function SetCaseCompletedPage() {
  const { productName, productId } = useProductParams();
  const productClassName = getProductNameBy("urlPartShort", productName);
  const productApiUri = getProductData(productClassName, "apiUri");
  const { data: product, isLoading } = useQuery({
    queryKey: queryKeys.item(productApiUri, productId),
    queryFn: () => fetchResource(productApiUri, productId),
  });
  const queryClient = useQueryClient();
  const updateMutation = useMutation({
    mutationFn: updateResource,
    onSuccess: (data, variables) => queryClient.setQueryData(queryKeys.item(variables.uri, variables.id), data),
  });
  const [additionalMessage, setAdditionalMessage] = useState("");
  const [isLoadingInternal, setIsLoadingInternal] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (
      product &&
      ![STATUS_EXTRAJUDICIAL_COMMUNICATION_SENT, STATUS_COMPLETED].includes(product.leadStatus) &&
      !(product.name === "civilServiceLaw" && product.leadStatus === STATUS_ADMINISTRATIVE_PROCEEDINGS)
    ) {
      navigate(getCaseLink(product));
    }
  }, [product]);

  if (isLoading) {
    return (
      <LayoutProtectedRoutes>
        <LegalbirdLoader centered />
      </LayoutProtectedRoutes>
    );
  }

  const handleSubmit = async () => {
    setIsLoadingInternal(true);

    let payload: { setCaseCompleted: boolean; additionalMessage?: string } = {
      setCaseCompleted: true,
    };

    if (additionalMessage) {
      payload.additionalMessage = additionalMessage;
    }

    await updateMutation.mutateAsync({
      uri: productApiUri,
      id: product.id,
      data: payload,
    });
    setIsLoadingInternal(false);
  };

  if (product.leadStatus === STATUS_COMPLETED) {
    return (
      <LayoutProtectedRoutes>
        <ScrollToTop />
        <HintBox headline={"Fall abgeschlossen"}>
          <Typography>Vielen Dank für Ihre Rückmeldung. Ihr Fall ist nun abgeschlossen. Wir wünschen Ihnen alles Gute!</Typography>
          <Button component={Link} to={"/servicewelt"} variant={"outlined"} sx={{ marginTop: "1rem" }}>
            Zurück zur Übersicht
          </Button>
        </HintBox>
      </LayoutProtectedRoutes>
    );
  }

  return (
    <LayoutProtectedRoutes>
      <HintBox headline={"Fall abschließen"}>
        <Typography>
          Es kam zu einer Einigung mit der Gegenseite und der Fall kann abgeschlossen werden? Dann bestätigen Sie uns dies bitte kurz. Wir schließen den Fall
          dann gerne entsprechend ab.
        </Typography>
      </HintBox>
      <Paper>
        <TextContainer>
          <Stack
            spacing={3}
            sx={{
              alignItems: "center",
            }}
          >
            <Typography variant={"h5"}>Möchten Sie uns noch etwas Ergänzendes mitteilen?</Typography>
            <TextField
              label={"Ihr Anliegen"}
              placeholder={"Ihre Nachricht an uns (optional)"}
              multiline
              value={additionalMessage}
              onChange={(e) => setAdditionalMessage(e.target.value.substring(0, 750))}
              rows={10}
              fullWidth
              helperText={"[" + additionalMessage.length + "/750]"}
            />
            <ButtonLoading variant={"contained"} onClick={handleSubmit} isLoading={isLoadingInternal} fullWidth>
              Fall abschließen
            </ButtonLoading>
          </Stack>
        </TextContainer>
      </Paper>
    </LayoutProtectedRoutes>
  );
}
